import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import * as ReactDOM from 'react-dom';
const isServer = typeof document === 'undefined';
const supportsDSD = !isServer && 'shadowRootMode' in HTMLTemplateElement.prototype;

/**
 * We can construct a shadow DOM in two ways:

 * - Imperatively with javascript: the content will not be rendered immediately.
 * - Declaratively with html: the content is rendered when the page first loads.
 *
 * Ideally we want to use the declarative approach in order to:
 *
 * - avoid an initial white screen while client-side javascript is constructing the shadow DOM.
 * - have the page fully rendered on the server.
 *
 * However, there are shortcomings with this in next.js/react related to hydration.
 * See: https://github.com/vercel/next.js/discussions/51717 and https://fullystacked.net/declarative-shadow-dom/.
 * For this reason, we need a more robust solution that can gracefully transition between the imperative and declarative approaches depending if we're on the client or server. The code presented here was based on https://github.com/mayank99/declarative-shadow-dom/.
 */
export default function ShadowRoot(t0) {
  const $ = _c(8);
  const {
    children,
    mode: t1
  } = t0;
  const mode = t1 === undefined ? "open" : t1;
  const isClient = useIsClient();
  if (isClient) {
    let t2;
    if ($[0] !== children || $[1] !== mode) {
      t2 = <ClientShadowRoot mode={mode}>{children}</ClientShadowRoot>;
      $[0] = children;
      $[1] = mode;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (supportsDSD) {
    return null;
  }
  let t2;
  if ($[3] !== mode) {
    t2 = {
      shadowrootmode: mode
    };
    $[3] = mode;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <template {...t2}>{children}</template>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ClientShadowRoot(t0) {
  const $ = _c(6);
  const {
    children,
    mode: t1
  } = t0;
  const mode = t1 === undefined ? "open" : t1;
  const templateRef = React.useRef(null);
  const [shadowRoot, setShadowRoot] = React.useState(null);
  let t2;
  let t3;
  if ($[0] !== mode) {
    t2 = () => {
      const parent = templateRef.current?.parentElement;
      if (!parent) {
        return;
      }
      if (parent.shadowRoot) {
        parent.shadowRoot.replaceChildren();
      }
      queueMicrotask(() => {
        ReactDOM.flushSync(() => {
          setShadowRoot(parent.shadowRoot ?? parent.attachShadow({
            mode
          }));
        });
      });
      return () => setShadowRoot(null);
    };
    t3 = [mode];
    $[0] = mode;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  React.useLayoutEffect(t2, t3);
  let t4;
  if ($[3] !== children || $[4] !== shadowRoot) {
    t4 = shadowRoot ? ReactDOM.createPortal(children, shadowRoot) : <template ref={templateRef} />;
    $[3] = children;
    $[4] = shadowRoot;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
const noopSubscribe = () => () => {};
function useIsClient() {
  return React.useSyncExternalStore(noopSubscribe, _temp, _temp2);
}
function _temp2() {
  return false;
}
function _temp() {
  return true;
}